import React, { forwardRef, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import Icon from '@mdi/react';
import { mdiArrowLeft, mdiClose, mdiExpandAll, mdiTextBoxOutline, mdiUnfoldMoreHorizontal, mdiUnfoldMoreVertical } from '@mdi/js';
import _ from 'lodash';
import { IconButton } from '../Button/IconButton';
import { getReportTypeDescription, getReportTypeIcon } from './utils';
import { getAvailableReportTypes } from '../../providers/ReportsProvider/utils';
import { useVisibilityState } from '../../hooks/useVisibilityState';
import { Link } from '../Link/Link';
import graphSheets from '../../assets/graph_sheets.png';
import { Drawer } from '../Accordion/Accordion';
import { useToast } from '../../hooks/useToast';
import { ReportTemplateCard } from '../ReportTemplates/ReportTemplateCard';
import { useMergedRefs } from '../../hooks/useMergedRefs';
import { Button } from '../Button/Button';
import { useGridColumnCount } from '../../hooks/useGridColumnCount';
import { useReportTemplates } from '../ReportTemplates/queries';

export function NewReportDrawer({ open, onClose, className }: { open: boolean, onClose?: () => void, className?: string }) {
	const [isOpen, setIsOpen] = useState(open);
	const templatesContainer = useRef<HTMLDivElement | null>(null);
	const columnCount = useGridColumnCount(templatesContainer);

	const { data: templates = [], isLoading } = useReportTemplates({ enabled: !!isOpen });

	useEffect(() => {
		setIsOpen(open);
	}, [open]);

	const reportTypes = getAvailableReportTypes().filter((t) => !t.noSaveType);
	const reportTypeCards = useMemo(() => reportTypes.map((t) => ({
		type: t.type,
		label: t.DESCRIPTION,
		icon: getReportTypeIcon(t.type),
		description: getReportTypeDescription(t.type),
		link: `${t.path}/add`,
	})), [reportTypes]);

	const handleClose = () => {
		setIsOpen(false);
		onClose?.();
	};

	const visibleTemplates = useMemo(() => {
		if (templates.length === 0 || columnCount === 0) {
			return [];
		}
		// Since we don't have any ordering of templates, pick a good representation of them by picking one from each type in order
		const templatesByType = _.groupBy(templates, 'settings.type');
		const reportTypeOrder = reportTypes.map((rt) => rt.type);
		const interleaved = _.flatten(_.zip(..._.take(reportTypeOrder.map((type) => templatesByType[type]), columnCount)));
		// Limit to the number of columns we can fit
		return _.take(interleaved, columnCount);
	}, [templates, columnCount, reportTypes]);

	return (
		<Drawer
			open={isOpen}
			className={clsx(
				'-mx-12', // HACK: Negative margin to make bg reach full width
				className,
			)}
		>
			<div className="pt-6 pb-8 px-12 bg-bg-static-medium">
				<div className="mb-4 flex w-full flex-row items-center justify-between gap-4 px-6">
					<h1 className="font-medium leading-6 text-text-primary">Create a new report</h1>
					<IconButton
						size="s"
						icon={<Icon path={mdiClose} />}
						onClick={handleClose}
					/>
				</div>

				<div className="grid grid-cols-[repeat(auto-fill,minmax(270px,1fr))] gap-6 mb-6">
					{reportTypeCards.map((card) => (
						<Link
							unstyled
							className="flex w-full flex-col gap-4 rounded-lg border border-borders-primary bg-bg-interactive-light-bg-active_default px-6 py-4 text-left transition-shadow hover:border-borders-primary_hover hover:shadow-[0px_4px_7px_2px_rgba(25,25,25,0.05)] active:border-borders-primary_pressed"
							href={card.link}
							key={card.type}
						>
							<div className="flex flex-row gap-[10px] items-center">
								<img src={card.icon} alt={card.label} className="size-5" />
								<span className="text-sm font-semibold text-text-primary truncate">{card.label}</span>
							</div>
							<div className="w-full h-[1px] bg-borders-primary" />
							<div className="text-xs text-text-secondary">{card.description}</div>
						</Link>
					))}
				</div>

				<h2 className="mb-4 text-base font-medium text-text-primary px-6">Use a template</h2>

				<div
					ref={templatesContainer}
					className="mb-6 grid grid-cols-[repeat(auto-fill,minmax(270px,1fr))] gap-6"
				>
					{isLoading ? (
						// Show skeleton cards while loading
						Array.from({ length: columnCount }).map((_, index) => (
							<ReportTemplateCard
								// eslint-disable-next-line react/no-array-index-key
								key={`skeleton-${index}`}
								skeleton
							/>
						))
					) : (
						visibleTemplates.map((template) => (
							<ReportTemplateCard
								key={template.id}
								template={template}
							/>
						))
					)}
				</div>

				<div className="flex justify-center">
					<Button variant="ghost" size="s" href="/reports/templates" hrefState={{ backlink: { href: '/reports', hrefState: { new: true } } }}>View all templates</Button>
				</div>
			</div>
		</Drawer>
	);
}
